/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import {
  getOperatorGamesList,
  deleteOperatorGame,
  testLaunchOperatorGame,
} from "services/operatorGameService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import { defaultPageNo } from "utils/constants";
import { sortArray } from "utils/miscUtils";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useConsent from "hooks/useConsent";
import usePaginationHook from "hooks/usePagination";
import { SimpleSelectInput } from "components/FormSelectInput";
import TestLaunchDialog from "components/TestLaunchDialog";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  filtersContainer: {
    marginBottom: "24px",
    padding: "16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  filterTitle: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "12px",
  },
}));

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { handleDelete = () => {}, handleTestLaunch = () => {} } =
    actionHandlers;

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.operatorGames.edit?.replace(":id", data?._id));
  };

  const handleTestLaunchClick = (event: any, data: any) => {
    handleClose(event);
    handleTestLaunch(data);
  };

  const handleDeleteClick = (event: any, data: any) => {
    handleClose(event);
    handleDelete(data);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR_GAMES.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleRowAction(e, data)}>Edit</MenuItem>
        <MenuItem onClick={(e) => handleTestLaunchClick(e, data)}>
          Test Launch
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={(e) => handleDeleteClick(e, data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const OperatorGamesList = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [operatorGamesList, setOperatorGamesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [filters, setFilters] = useState({
    platform: "",
    operator: "",
    brand: "",
    isActive: "",
  });
  const [testLaunchDialogOpen, setTestLaunchDialogOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [testLaunchLoading, setTestLaunchLoading] = useState(false);

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR_GAMES?.CREATE_ROLES,
  );
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR_GAMES?.EDIT_ROLES,
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: totalCount });

  // Get filter options from sessionHelper
  const platformOptions = sessionHelper.platformOptions || [];
  const operatorOptions = sessionHelper.operatorOptions || [];
  const brandOptions = sessionHelper.brandOptions || [];

  const columns = [
    {
      id: "_id",
      label: "ID",
      type: "string",
      isSortable: false,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "gameName",
      label: "Game",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "gameId",
      label: "Master Game ID",
      type: "string",
      isSortable: false,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "platformName",
      label: "Platform",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "operatorName",
      label: "Operator",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "brandName",
      label: "Brand",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "isActiveText",
      label: "Active",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "actions",
      label: "Actions",
      type: "string",
      isSortable: false,
      alignHead: "left",
      alignBody: "left",
    },
  ];

  const onColumnClick = (column: any) => {
    let _list = sortArray([...operatorGamesList], orderDirection, column);
    setOperatorGamesList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        pageNo,
        pageSize,
        ...(filters.platform && { platform: filters.platform }),
        ...(filters.operator && { operator: filters.operator }),
        ...(filters.brand && { brand: filters.brand }),
        ...(filters.isActive !== "" && { isActive: filters.isActive }),
      };

      const resp = await getOperatorGamesList(params);
      const operatorGames = resp?.data?.data || [];

      // Transform data for table display
      const transformedGames = operatorGames.map((game) => ({
        ...game,
        // Extract names and IDs from populated objects or use ID as fallback
        platformName:
          typeof game.platform === "object" && game.platform !== null
            ? `${game.platform.platformName || "N/A"} (${
                game.platform._id || "N/A"
              })`
            : game.platform || "N/A",
        operatorName:
          typeof game.operator === "object" && game.operator !== null
            ? `${game.operator.operatorName || "N/A"} (${
                game.operator._id || "N/A"
              })`
            : game.operator || "N/A",
        brandName:
          typeof game.brand === "object" && game.brand !== null
            ? `${game.brand.brandName || "N/A"} (${game.brand._id || "N/A"})`
            : game.brand || "N/A",
        isActiveText: game?.isActive ? "YES" : "NO",
        rowDisabled: !game?.isActive,
      }));

      setOperatorGamesList(transformedGames);
      setTotalCount(transformedGames.length || 0);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    onChangePage(defaultPageNo);
  };

  const _openConsentDialog = (data: any) => {
    openConsent();

    // Build detailed information string
    const details = [
      `Platform: ${data?.platformName || "N/A"}`,
      `Operator: ${data?.operatorName || "N/A"}`,
      `Brand: ${data?.brandName || "N/A"}`,
      `Game ID: ${data?.gameId || data?._id || "N/A"}`,
    ].join(" | ");

    updateDetails({
      itemName: data?.gameName || data?.game?.gameName || data?._id,
      itemDetails: details,
      id: data?._id,
    });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteOperatorGame(constentDetails?.id);
      showSnackbar(
        resp?.message || "Operator game deleted successfully",
        "success",
      );
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const handleCreate = () => {
    navigate(configData.routes.operatorGames.create);
  };

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.operatorGames.edit?.replace(":id", data?._id)
      : null;

  const openTestLaunchDialog = (data: any) => {
    setSelectedGame(data);
    setTestLaunchDialogOpen(true);
  };

  const closeTestLaunchDialog = () => {
    setTestLaunchDialogOpen(false);
    setSelectedGame(null);
    setTestLaunchLoading(false);
  };

  const handleTestLaunch = async (formData: any, isDebugBuild: boolean) => {
    try {
      setTestLaunchLoading(true);
      const payload = {
        operatorGameId: selectedGame?._id,
        ...formData,
      };

      const resp = await testLaunchOperatorGame(payload);
      let launchUrl = resp?.data?.launchUrl;

      if (launchUrl) {
        // Append isDebugBuild parameter to the launch URL
        const separator = launchUrl.includes("?") ? "&" : "?";
        launchUrl = `${launchUrl}${separator}isDebugBuild=${isDebugBuild}`;

        // Open the launch URL in a new tab
        window.open(launchUrl, "_blank");
        showSnackbar(resp?.message || "Game launched successfully", "success");
        closeTestLaunchDialog();
      } else {
        showSnackbar("Launch URL not found in response", "error");
        setTestLaunchLoading(false);
      }
    } catch (error) {
      showSnackbar(error.message, "error");
      setTestLaunchLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, filters]);

  return (
    <ContentShell title="Operator Games Management">
      <Box className={classes.container}>
        {/* Filters Section */}
        <Box className={classes.filtersContainer}>
          <div className={classes.filterTitle}>Filters</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SimpleSelectInput
                label="Platform"
                name="platform"
                value={filters.platform}
                onChange={handleFilterChange}
                options={[
                  { id: "", label: "All Platforms" },
                  ...platformOptions,
                ]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SimpleSelectInput
                label="Operator"
                name="operator"
                value={filters.operator}
                onChange={handleFilterChange}
                options={[
                  { id: "", label: "All Operators" },
                  ...operatorOptions,
                ]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SimpleSelectInput
                label="Brand"
                name="brand"
                value={filters.brand}
                onChange={handleFilterChange}
                options={[{ id: "", label: "All Brands" }, ...brandOptions]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SimpleSelectInput
                label="Status"
                name="isActive"
                value={filters.isActive}
                onChange={handleFilterChange}
                options={[
                  { id: "", label: "All" },
                  { id: "true", label: "Active" },
                  { id: "false", label: "Inactive" },
                ]}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.header}>
          <div />
          {hasCreatePermission && (
            <CustomButton
              variant="contained"
              onClick={handleCreate}
              label="Create Operator Game"
            />
          )}
        </Box>

        <Table
          columns={columns}
          data={operatorGamesList}
          actions={RowActions}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleTestLaunch: openTestLaunchDialog,
          }}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalCount}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          highlightAltRow={true}
          rowHref={rowHref}
        />

        <DeleteConsentDialog
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
          itemName={constentDetails?.itemName}
          itemDetails={constentDetails?.itemDetails}
        />

        <TestLaunchDialog
          open={testLaunchDialogOpen}
          onClose={closeTestLaunchDialog}
          onLaunch={handleTestLaunch}
          loading={testLaunchLoading}
        />
      </Box>
    </ContentShell>
  );
};

export default OperatorGamesList;
